/**
 *  @swagger
 *  definitions:
 *    RegattaResultTableRaceResultParams:
 *      type: object
 *      properties:
 *        RegattaResultTableRaceId:
 *          description: ID of the associated RegattaResultTableRace
 *          type: string
 *        RegattaResultTableEntryId:
 *          description: ID of the associated RegattaResultTableEntry
 *          type: string
 *        Position:
 *          description: Position of the RegattaResultTableRaceResult
 *          type: string
 *        Month:
 *          description: Month of the RegattaResultTableRaceResult
 *          type: integer
 *        Day:
 *          description: Day of the RegattaResultTableRaceResult
 *          type: integer
 *        Hour:
 *          description: Hour of the RegattaResultTableRaceResult
 *          type: integer
 *        Minute:
 *          description: Minute of the RegattaResultTableRaceResult
 *          type: integer
 *        Second:
 *          description: Second of the RegattaResultTableRaceResult
 *          type: integer
 *        InputDate:
 *          description: Input Date of the RegattaResultTableRaceResult
 *          type: string
 *          format: date-time
 *        ET:
 *          description: ET of the RegattaResultTableRaceResult
 *          type: integer
 *        CT:
 *          description: CT of the RegattaResultTableRaceResult
 *          type: integer
 *        Discarded:
 *          description: Discarded of the RegattaResultTableRaceResult
 *          type: boolean
 *        Place:
 *          description: Place of the RegattaResultTableRaceResult
 *          type: string
 *        Points:
 *          description: Points of the RegattaResultTableRaceResult
 *          type: integer
 *
 *  @swagger
 *  definitions:
 *    RegattaResultTableRaceResultCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaResultTableRaceResultParams'
 *        - type: object
 *          required:
 *            - RegattaResultTableRaceId
 *            - RegattaResultTableEntryId
 *            - ET
 *            - CT
 *            - Discarded
 *            - Points
 *
 *  @swagger
 *  definitions:
 *    RegattaResultTableRaceResultQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the RegattaResultTableRaceResult
 *              type: string
 *        - $ref: '#/definitions/RegattaResultTableRaceResultParams'
 *
 *  @swagger
 *  definitions:
 *    RegattaResultTableRaceResultUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaResultTableRaceResultQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    RegattaResultTableRaceResult:
 *      allOf:
 *        - $ref: '#/definitions/RegattaResultTableRaceResultCreateParams'
 *        - $ref: '#/definitions/RegattaResultTableRaceResultUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      RegattaResultTableRaceResult:
 *        allOf:
 *          - $ref: '#/definitions/RegattaResultTableRaceResult'
 *          - type: object
 *            properties:
 *              RegattaResultTableRace:
 *                description: RegattaResultTableRace of the RegattaResultTableRaceResult
 *                $ref: '#/components/schemas/RegattaResultTableRace'
 *              RegattaResultTableEntry:
 *                description: RegattaResultTableEntry of the RegattaResultTableRaceResult
 *                $ref: '#/components/schemas/RegattaResultTableEntry'
 */

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  RegattaResultTableEntryId: string;
  RegattaResultTableRaceId: string;
};

export type $Params = {
  CT: number;
  Day: null | number;
  Discarded: boolean;
  ET: number;
  Hour: null | number;
  InputDate: Date | null;
  Minute: null | number;
  Month: null | number;
  Place: null | string;
  Points: number;
  Position: null | string;
  Second: null | number;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  RegattaResultTableEntry?: $Model<'RegattaResultTableEntry'>;
  RegattaResultTableRace?: $Model<'RegattaResultTableRace'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  CT: {
    allowNull: false,
    type: Sequelize.INTEGER,
  },
  Day: {
    allowNull: true,
    type: Sequelize.INTEGER.UNSIGNED,
  },
  Discarded: {
    allowNull: false,
    defaultValue: false,
    type: Sequelize.BOOLEAN,
  },
  ET: {
    allowNull: false,
    type: Sequelize.INTEGER.UNSIGNED,
  },
  Hour: {
    allowNull: true,
    type: Sequelize.INTEGER.UNSIGNED,
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  InputDate: {
    allowNull: true,
    type: Sequelize.DATE,
    validate: {
      notEmpty: true,
    },
  },
  Minute: {
    allowNull: true,
    type: Sequelize.INTEGER.UNSIGNED,
  },
  Month: {
    allowNull: true,
    type: Sequelize.INTEGER.UNSIGNED,
  },
  Place: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Points: {
    allowNull: false,
    type: Sequelize.DOUBLE(
      4,
      1,
    ),
  },
  Position: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Second: {
    allowNull: true,
    type: Sequelize.INTEGER.UNSIGNED,
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'RegattaResultTableRaceResult'> => {
  const RegattaResultTableRaceResult = stack.define(
    'RegattaResultTableRaceResult',
    fields(Sequelize),
    {
      tableName: 'RegattaResultTableRaceResults',
    },
  );

  RegattaResultTableRaceResult.associate = (models: $ModelClasses) => {
    models.RegattaResultTableRaceResult.belongsTo(
      models.RegattaResultTableRace,
      {
        as: 'RegattaResultTableRace',
        foreignKey: 'RegattaResultTableRaceId',
      },
    );
    models.RegattaResultTableRaceResult.belongsTo(
      models.RegattaResultTableEntry,
      {
        as: 'RegattaResultTableEntry',
        foreignKey: 'RegattaResultTableEntryId',
      },
    );
  };

  RegattaResultTableRaceResult.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return RegattaResultTableRaceResult;
});
